import React, { useEffect, useState } from 'react';
import {Table, message, Button} from 'antd';
import { useMutation } from '@apollo/client';
import { useVirtualAccountDailySnapshotQuery } from './hooks/useVirtualAccountDailySnapshotQuery';
import VirtualAccountDailySnapshotFilter from './components/VirtualAccountDailySnapshotFilter';
import { VirtualAccountDailySnapshotFilterType } from './types/VirtualAccountDailySnapshotFilterType';
import {
    CREATE_VIRTUAL_ACCOUNT_DAILY_SNAPSHOT_DOWNLOAD_TASK,
    VirtualAccountDailySnapshotDownloadTaskResponse
} from './graphql/mutations/createVirtualAccountDailySnapshotDownloadTask';
import ParcelsDownloadModal from '../components/ParcelsDownloadModal';
import {HasResource} from "../../../../components/HasResource";
import {ResourceCode} from "../../../../constants/resourceCodes";
import {DownloadOutlined} from "@ant-design/icons";
import { resolvePricingSchemeMessage } from '../../../../utils/graphqlUtils';
import { formatDollarAmountFromCentsNoSpace } from '../../../../utils/settlementAmountFormatter';
import { formatInstantInIanaZone } from '../../../../utils/timezoneFormatter';
import { todayDateRange } from './config/filterOptions';

const defaultSnapshotFilters = (): VirtualAccountDailySnapshotFilterType => {
    const [from, to] = todayDateRange();
    return { snapshotDateFrom: from, snapshotDateTo: to };
};

const VirtualAccountDailySnapshotPage: React.FC = () => {
    const [tempFilters, setTempFilters] = useState<VirtualAccountDailySnapshotFilterType>(defaultSnapshotFilters());
    const [filters, setFilters] = useState<VirtualAccountDailySnapshotFilterType>(defaultSnapshotFilters());
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');

    const {
        snapshotData,
        loading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useVirtualAccountDailySnapshotQuery(filters, currentPage, pageSize);

    const [createDownloadTask, { loading: downloadLoading }] = useMutation<VirtualAccountDailySnapshotDownloadTaskResponse>(
        CREATE_VIRTUAL_ACCOUNT_DAILY_SNAPSHOT_DOWNLOAD_TASK
    );

    const columns = [
        {
            title: '客户名称',
            dataIndex: 'clientName',
            key: 'clientName',
            width: 150,
            ellipsis: true,
        },
        {
            title: '快照日期',
            dataIndex: 'snapshotDate',
            key: 'snapshotDate',
            width: 120,
            sorter: true,
        },
        {
            title: '虚拟账户编号',
            dataIndex: 'virtualAccountNo',
            key: 'virtualAccountNo',
            width: 150,
            ellipsis: true,
        },
        {
            title: '账户类型',
            dataIndex: 'accountType',
            key: 'accountType',
            width: 120,
        },
        {
            title: '余额(USD)',
            dataIndex: 'balance',
            key: 'balance',
            width: 120,
            sorter: true,
            render: (balance: string) => (
                <span style={{ color: parseFloat(balance) < 0 ? '#ff4d4f' : 'inherit' }}>
                    {formatDollarAmountFromCentsNoSpace(balance)}
                </span>
            ),
        },
        {
            title: '透支额度(USD)',
            dataIndex: 'overdraftLimit',
            key: 'overdraftLimit',
            width: 120,
            render: (limit: string) => formatDollarAmountFromCentsNoSpace(limit),
        },
        {
            title: '币种',
            dataIndex: 'currency',
            key: 'currency',
            width: 80,
        },
        {
            title: '客户时区',
            dataIndex: 'clientTimezone',
            key: 'clientTimezone',
            width: 100,
        },
        {
            title: '快照时间 (UTC+8)',
            dataIndex: 'snapshotTime',
            key: 'snapshotTime',
            width: 160,
            render: (snapshotTime: string) =>
                snapshotTime ? formatInstantInIanaZone(new Date(snapshotTime), 'Asia/Shanghai') : '-',
        },
    ];

    const handleReset = () => {
        setTempFilters(defaultSnapshotFilters());
        setFilters(defaultSnapshotFilters());
        setCurrentPage(1);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        // 处理表格排序和筛选
        console.log('Table changed:', { pagination, filters, sorter });
    };

    useEffect(() => {
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize]);

    const handleDownload = async () => {
        try {
            const result = await createDownloadTask({
                variables: {
                    filters: {
                        clientId: filters.clientId,
                        snapshotDateFrom: filters.snapshotDateFrom,
                        snapshotDateTo: filters.snapshotDateTo,
                        accountType: filters.accountType
                    }
                }
            });

            const createResult = result.data?.createVirtualAccountDailySnapshotDownloadTask;
            const fileName = createResult?.data
            if (!fileName) {
                message.error(resolvePricingSchemeMessage(createResult, '创建下载任务失败，请稍后重试'));
                return;
            }
            setGeneratedFileName(fileName);
            setDownloadCompleteModalVisible(true);
        } catch (error: any) {
            console.error('创建下载任务失败:', error);
            message.error(`创建下载任务失败: ${error.message || '未知错误'}`);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm p-6">
            {/* 筛选条件 */}
            <VirtualAccountDailySnapshotFilter
                filters={tempFilters}
                setFilters={setTempFilters}
                handleFilter={handleFilter}
                handleResetFilters={handleReset}
                downloadLoading={downloadLoading}
                onDownload={handleDownload}
            />

            {/* 操作按钮 */}
            <div className="mb-4 flex justify-end">
                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REALTIME_SNAPSHOT]}>
                    <Button icon={<DownloadOutlined />} loading={downloadLoading} onClick={handleDownload}>
                        导出数据
                    </Button>
                </HasResource>
            </div>

            {/* 数据表格 */}
            <Table
                columns={columns}
                dataSource={snapshotData}
                loading={loading}
                rowKey="id"
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalElements,
                    onChange: handlePageChange,
                    onShowSizeChange: (_, size) => {
                        setPageSize(size);
                        setCurrentPage(1);
                    },
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => 
                        `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
                }}
                onChange={handleTableChange}
                scroll={{ x: 1200 }}
                size="middle"
            />

            <ParcelsDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default VirtualAccountDailySnapshotPage;